<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { DxTabPanel, DxItem } from 'devextreme-vue/tab-panel';
import { BottoPagePanel } from '@botto/core';
import BotPoliciesPage from './BotPoliciesPage.vue';
import GeoDatabasesPage from './GeoDatabasesPage.vue';

const route = useRoute();
const router = useRouter();

const TAB_PATHS = [
  '/tracker/settings/bot-policies',
  '/tracker/settings/geo',
] as const;

function indexFromPath(path: string): number {
  const i = TAB_PATHS.findIndex((p) => path === p || path.startsWith(`${p}/`));
  return i >= 0 ? i : 0;
}

const selectedIndex = ref(indexFromPath(route.path));

watch(
  () => route.path,
  (path) => {
    const next = indexFromPath(path);
    if (next !== selectedIndex.value) selectedIndex.value = next;
  },
);

function onTabChanged(e: { component?: { option: (n: string) => number } }) {
  const idx = e.component?.option('selectedIndex') ?? selectedIndex.value;
  selectedIndex.value = idx;
  const target = TAB_PATHS[idx] ?? TAB_PATHS[0];
  if (route.path !== target) {
    void router.push(target);
  }
}

const pageTitle = computed(() =>
  selectedIndex.value === 1 ? 'Настройки · Базы GEO' : 'Настройки · Политики ботов',
);
</script>

<template>
  <BottoPagePanel :title="pageTitle" standalone>
    <template #header>
      <p class="settings-hint">
        Управление шаблонами детекции ботов и базами GEO / IP2Proxy. Доступно только администраторам
        (<code>admin:all</code>).
      </p>
    </template>

    <div class="settings-page">
      <DxTabPanel
        class="settings-tabs"
        height="100%"
        :selected-index="selectedIndex"
        :defer-rendering="false"
        :animation-enabled="true"
        :swipe-enabled="false"
        @selection-changed="onTabChanged"
      >
        <DxItem title="Политики ботов">
          <div class="settings-tab-body">
            <BotPoliciesPage />
          </div>
        </DxItem>
        <DxItem title="Базы GEO">
          <div class="settings-tab-body">
            <GeoDatabasesPage />
          </div>
        </DxItem>
      </DxTabPanel>
    </div>
  </BottoPagePanel>
</template>

<style scoped>
.settings-hint {
  margin: 0;
  font-size: 0.875rem;
  color: var(--dx-color-text-secondary, #666);
}

.settings-page {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.settings-tabs {
  min-height: 0;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
}

.settings-tabs :deep(.dx-tabpanel-container),
.settings-tabs :deep(.dx-multiview-wrapper),
.settings-tabs :deep(.dx-multiview-item-content) {
  height: 100%;
}

.settings-tab-body {
  height: 100%;
  min-height: 0;
  overflow: auto;
  padding: 0.75rem 0.15rem 0.5rem;
}
</style>
